/**
 * Product-grid skeleton controller for Nosto merchandising.
 *
 * Shared by the collection grid (category merchandising) and the search results grid —
 * the behaviour is identical, only the section wrapper differs, and the snippet passes
 * that in as `containerSelector`.
 *
 * The native cards are hidden behind a skeleton on first paint (armed by the inline
 * bootstrap in snippets/nosto-grid-skeleton.liquid, which also owns the reveal
 * function, the bfcache handler and a failsafe timer so the page still works if this file
 * never executes). This controller decides *when* to reveal:
 *
 *   - Nosto's cards are in and rendered  → reveal, and the shopper never sees the native grid.
 *   - Nosto says it rendered nothing     → reveal the native grid immediately.
 *   - Nosto's script failed to load      → reveal the native grid immediately.
 *   - Nothing happened before `timeout`  → reveal the native grid as the fallback, and
 *                                          discard anything Nosto renders after that.
 *
 * It checks card STATE rather than counting `@nosto/DynamicCard/loaded` events: cards can
 * settle before we attach a listener, Nosto injects them asynchronously so the expected
 * count is unknown up front, and a card that throws sets `error` and rethrows WITHOUT ever
 * dispatching `loaded`. Events and mutations are only nudges to re-run the state check.
 *
 * Note on detecting a blocked Nosto: this theme does NOT use Nosto's inline stub.
 * snippets/__opt-theme-headers.liquid injects it as <script async src="connect.nosto.com/...">,
 * so window.nostojs does not exist until that remote script lands. "nostojs is undefined
 * right now" therefore means "not loaded YET", never "blocked" — checking it once at startup
 * would call every ordinary load a failure. Blocking is detected from the script element's
 * error event, and the timeout covers a request that hangs without erroring.
 */
(function () {
  // Cast, not an assumption: the guard on the next line is the real check. It is here
  // because `var` narrowing does not reach the closures below, which would otherwise read
  // as possibly-undefined under checkJs despite being unreachable until S exists.
  var S = /** @type {NostoGridSkeleton} */ (window.NostoGridSkeleton);
  if (!S || S.revealed) return;

  // morphSection() re-inserts this script tag on every filter and sort, which re-executes
  // it. Without this guard each pass would stack another observer and another timer set.
  if (S.controllerAttached) return;
  S.controllerAttached = true;

  var cfg = S.config;
  var LOADED_EVENT = '@nosto/DynamicCard/loaded';
  var CARD_SELECTOR = 'nosto-dynamic-card';
  var VIEWPORT_MARGIN = 100;
  var MIN_CARDS_WHEN_NONE_VISIBLE = 4;
  var POLL_INTERVAL = 250;
  // Once a card's markup lands its images still have to load. Revealing at that moment
  // shows an empty bordered card that fills in a beat later — the skeleton is supposed to
  // cover exactly that. Bounded, so a slow image cannot hold the grid on its own.
  var IMAGE_GRACE = 700;
  // `postrender` fires when Nosto has finished rendering its placements, which for category
  // merchandising is the same tick the cards go in. Acting on it instantly would race the
  // injection and call a working page empty, so give the DOM a beat to catch up.
  var POSTRENDER_GRACE = 600;

  // Cast for the same reason as S above — the guard below is what actually proves it.
  var container = /** @type {Element} */ (document.querySelector(cfg.containerSelector));
  if (!container) {
    S.reveal('no-container');
    return;
  }

  // Nosto's templates clear the section when they mount. Keeping the native nodes lets the
  // fallback in snippets/nosto-grid-skeleton.liquid put them back if Nosto then misses the
  // deadline.
  S.nativeNodes = Array.prototype.slice.call(container.childNodes);

  var grid = container.querySelector('.product-grid');
  if (grid) grid.setAttribute('aria-busy', 'true');

  var markupReadyAt = null;
  var postrenderAt = null;
  var nostoQueued = false;
  var scheduled = false;
  var timers = [];
  var pollTimer = null;

  function later(fn, ms) {
    timers.push(setTimeout(fn, ms));
  }

  function enforceDeadline() {
    if (S.revealed) return;
    var now = performance.now();

    // Nosto's own "I finished rendering" signal. Without it a page where category
    // merchandising simply is not configured would sit on the skeleton for the whole
    // timeout; with it the native grid is back almost immediately.
    if (postrenderAt !== null && now - postrenderAt >= POSTRENDER_GRACE && !container.querySelector(CARD_SELECTOR)) {
      S.reveal('nosto-no-cards');
      return;
    }

    if (now - S.startedAt >= cfg.maxTimeout) {
      S.reveal('timeout', { sinceStart: Math.round(now - S.startedAt) });
    }
  }

  /**
   * DynamicCard reflects `loading` / `error` / `empty`. Depending on the build, a
   * cleared flag is either the attribute removed or set to the string "false", so
   * treat both as off.
   */
  function flagOn(el, name) {
    return el.hasAttribute(name) && el.getAttribute(name) !== 'false';
  }

  function cardSettled(card) {
    // A card that failed or came back blank is finished, not still loading —
    // otherwise one bad card holds the skeleton open until the timeout.
    if (flagOn(card, 'error') || flagOn(card, 'empty')) return true;
    if (flagOn(card, 'loading')) return false;
    // No state flag yet: the custom element may not have upgraded. Nosto replaces the
    // element's child content when it renders, so a surviving placeholder means the
    // card has not rendered regardless of what the attributes say.
    if (card.querySelector('.product-card-skeleton')) return false;
    return !!card.firstElementChild;
  }

  /**
   * Only images the shopper can actually see are worth waiting on. Scoping by viewport
   * rather than by the `loading` attribute covers both cases: a lazy image on screen is
   * already fetching and worth the wait, one below the fold may never start.
   */
  function imagesReady(cards) {
    for (var i = 0; i < cards.length; i++) {
      var images = cards[i].querySelectorAll('img');
      for (var j = 0; j < images.length; j++) {
        if (!images[j].complete && inViewport(images[j])) return false;
      }
    }
    return true;
  }

  function inViewport(el) {
    var rect = el.getBoundingClientRect();
    if (!rect.width && !rect.height) return false;
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.top < viewportHeight + VIEWPORT_MARGIN && rect.bottom > -VIEWPORT_MARGIN;
  }

  /**
   * With `lazy` enabled, below-the-fold cards never start loading until scrolled into
   * view, so "wait for every card" would never complete. Scoping the wait to the cards
   * actually on screen both survives that and shortens the skeleton.
   */
  function cardsToWaitFor(cards) {
    if (cfg.revealScope === 'all') return cards;
    var visible = cards.filter(inViewport);
    return visible.length ? visible : cards.slice(0, MIN_CARDS_WHEN_NONE_VISIBLE);
  }

  function check() {
    if (S.revealed) return;

    // A re-render can detach the section we are watching. Nothing left to hold open.
    if (!container.isConnected) {
      S.reveal('grid-replaced');
      return;
    }

    var cards = Array.prototype.slice.call(container.querySelectorAll(CARD_SELECTOR));
    if (!cards.length) return; // The timeout covers "Nosto never injected anything".

    var waiting = cardsToWaitFor(cards);
    if (!waiting.length) return;

    for (var i = 0; i < waiting.length; i++) {
      if (!cardSettled(waiting[i])) return;
    }

    // Markup is in. Hold the skeleton a little longer so the card is revealed complete
    // rather than as an empty frame, but never past IMAGE_GRACE.
    if (markupReadyAt === null) {
      markupReadyAt = performance.now();
      later(function () {
        S.reveal('cards-settled', { cards: cards.length, waitedOn: waiting.length, imagesPending: true });
      }, IMAGE_GRACE);
    }

    if (!imagesReady(waiting)) return;

    S.reveal('cards-settled', { cards: cards.length, waitedOn: waiting.length });
  }

  function safeCheck() {
    // Nothing in here is worth a broken page or a console error in front of QA.
    try {
      check();
    } catch (error) {
      S.reveal('check-failed', { error: String(error) });
    }
  }

  function schedule() {
    if (scheduled || S.revealed) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      safeCheck();
    });
  }

  // The loaded event is dispatched with bubbles: true, so one container listener
  // catches every card — no per-card wiring, and it survives Nosto replacing children.
  container.addEventListener(LOADED_EVENT, schedule);

  // Picks up cards Nosto injects after first paint, and the attribute flips that a
  // failing card makes without ever dispatching the loaded event.
  var observer = new MutationObserver(schedule);
  observer.observe(container, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['loading', 'error', 'empty'],
  });

  // Filtering and sorting both dispatch filter:update before re-rendering the section.
  // The shopper asked for different products; holding a skeleton over the results they
  // are waiting for would make the grid look broken. Get out of the way.
  function handleFilterUpdate() {
    S.reveal('filter-update');
  }
  document.addEventListener('filter:update', handleFilterUpdate);

  // Background tabs throttle timers and never fire rAF, so re-check on the way back.
  function handleVisibilityChange() {
    if (!document.hidden) schedule();
  }
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Backstop for anything the observer cannot see — most importantly the section being
  // detached wholesale, after which no mutation on it will ever fire again. Same belt
  // and braces as assets/nosto-recs-row.js; it stops at the first reveal either way.
  pollTimer = setInterval(function () {
    probeNosto();
    watchNostoScript();
    safeCheck();
    enforceDeadline();
  }, POLL_INTERVAL);

  S.onReveal(function () {
    container.removeEventListener(LOADED_EVENT, schedule);
    document.removeEventListener('filter:update', handleFilterUpdate);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    timers.forEach(clearTimeout);
    timers.length = 0;
    if (pollTimer) clearInterval(pollTimer);
    observer.disconnect();
  });

  // Nosto's script is injected async by snippets/__opt-theme-headers.liquid, so
  // window.nostojs appears at some unpredictable point after this file runs. Keep
  // looking (the poll drives this) rather than deciding on a single early check.
  function probeNosto() {
    if (nostoQueued || typeof window.nostojs !== 'function') return;
    nostoQueued = true;
    try {
      window.nostojs(function (api) {
        // Best effort — older builds do not expose listen, and the timeout covers it.
        if (!api || typeof api.listen !== 'function') return;
        api.listen('postrender', function () {
          if (postrenderAt === null) postrenderAt = performance.now();
          schedule();
        });
      });
    } catch (e) {
      nostoQueued = false;
    }
  }

  // A blocked request fires an error on the script element. That is proof, and it lands
  // far sooner than any timeout could. If the blocker works some other way (hung request,
  // stubbed response) the timeout is the backstop.
  function watchNostoScript() {
    var script = document.querySelector('script[src*="nosto.com"]');
    if (!script || script.dataset.nostoSkeletonWatched) return;
    script.dataset.nostoSkeletonWatched = '1';
    script.addEventListener('error', function () {
      S.reveal('nosto-blocked');
    });
  }

  probeNosto();
  watchNostoScript();
  safeCheck();
})();
